/**
 * EmptyLibraryState — shown when the user has zero recipes.
 *
 * Wave 2 (onboarding-empty-r1): this is the load-bearing new-user landing
 * surface. The guided wizard is wishlisted per
 * `.plan/wishlist/onboarding-wizard.md`.
 *
 * Frame 21 (MOBILE-LIBRARY-EMPTY) spec:
 *   - One declarative line of copy.
 *   - One primary CTA with Ember fill.
 *   - No illustration, no hero image, no decoration.
 *   - In-Progress section and collection-chip rows hidden.
 *
 * P1 amendment (plan 2026-06-10-pip-mascot-placement.md §3.5 + §6.4):
 *   design.md §13a.2 amended: "No illustrations or icons except Pip, the
 *   product mascot, whose empty-state placements are governed by the placement
 *   plan." The 64px resting Pip with idle-breath animation is placed above the
 *   existing copy. No copy changes.
 *
 * Design refs:
 *   design.md §2 — accent reserved for primary CTAs; quiet motion; no
 *     decoration (Pip exception per §13a.2 amendment).
 *   design.md §6.0a — discoverability; one labelled primary-nav CTA.
 *   design.md §6.0 — empty-state CTAs must suggest what the surface is for.
 *   branding.md §5 / §5.2.1 — brand voice; banned words list.
 *
 * Voice check (branding.md §5.2.1):
 *   "Your library is empty." — declarative, sentence-case, no exclamation.
 *   "Add recipe" — imperative verb phrase, matches in-app vocabulary (§8).
 *   No `branch`, `commit`, `fork`, `hash`, `merge`, `Let's`, `Awesome`,
 *   `Great`, `Oops`, `Magic`, `Seamless`, `Effortless`, emoji.
 *
 * CLAUDE.md Rule 7 — mobile + tablet + desktop from first commit.
 */

import { useCallback, useState } from "react";
import { PipMascot } from "~/components/pip/PipMascot";
import { withTimeout } from "~/lib/withTimeout";
import { FirstRunCallout } from "./FirstRunCallout";
import { useFirstRunCallout } from "./useFirstRunCallout";

// ── Styles ────────────────────────────────────────────────────────────────────

const wrapperStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  flex: "1 1 0",
  minHeight: "240px",
  padding: "var(--space-8, 32px) var(--space-4, 16px)",
  fontFamily: "var(--font-sans)",
  gap: "var(--space-4, 16px)",
  textAlign: "center",
};

const messageStyle: React.CSSProperties = {
  fontSize: "14px",
  color: "var(--muted)",
  lineHeight: 1.6,
  maxWidth: "280px",
  margin: 0,
};

const ctaStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  height: "36px",
  padding: "0 var(--space-4, 16px)",
  borderRadius: "var(--radius-pill, 999px)",
  fontFamily: "var(--font-sans)",
  fontSize: "13px",
  fontWeight: 500,
  // Accent background — primary CTA is the one permitted use of accent fill
  // per design.md §2 "Accent only on: … primary CTAs".
  background: "var(--accent)",
  color: "var(--surface)",
  border: "none",
  cursor: "pointer",
  // design.md §2: "Only permitted shadow: 0 1px 2px rgba(0,0,0,0.04) on the
  // primary CTA."
  boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
  transition: "opacity 200ms ease",
  WebkitTapHighlightColor: "transparent",
};

// ── Entry fork (Beat-0) styles ──────────────────────────────────────────────
// The empty-library surface presents a two-action fork (plan T3):
//   1. "Add recipe" — the accent-FILL primary (ctaStyle above; the one
//      permitted accent fill + micro-shadow per design.md §2).
//   2. "Open a worked example" — a secondary hairline action, NO fill, --text
//      colour, so the accent stays reserved for the primary.
// Stacked, centred, 44px tap targets (Rule 7); constrained width so the pair
// never overflows at 375px.

const forkStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "var(--space-2, 8px)",
  width: "100%",
  maxWidth: "280px",
};

// Secondary fork action — hairline, no fill, --text colour (design.md §2).
const workedExampleStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "44px",
  width: "100%",
  padding: "0 var(--space-4, 16px)",
  borderRadius: "var(--radius-pill, 999px)",
  fontFamily: "var(--font-sans)",
  fontSize: "13px",
  fontWeight: 500,
  color: "var(--text)",
  background: "none",
  border: "1px solid var(--line)",
  cursor: "pointer",
  transition: "border-color 200ms ease, color 200ms ease",
  WebkitTapHighlightColor: "transparent",
};

// Quiet inline error — matches the app's form-error treatment (--del), small,
// left of nothing else. Rule 8: the worked-example failure is visible here.
const workedExampleErrorStyle: React.CSSProperties = {
  fontFamily: "var(--font-sans)",
  fontSize: "12px",
  lineHeight: 1.5,
  color: "var(--del)",
  margin: 0,
  maxWidth: "280px",
  textAlign: "center",
};

const EMPTY_CSS = `
  .proof-empty-library-cta:hover {
    opacity: 0.88;
  }
  .proof-empty-library-cta:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }
  .proof-worked-example:hover:not(:disabled) {
    border-color: var(--text);
  }
  .proof-worked-example:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }
  .proof-worked-example:disabled {
    color: var(--faint);
    cursor: default;
  }
`;

if (typeof document !== "undefined") {
  const id = "proof-empty-library-css";
  if (!document.getElementById(id)) {
    const el = document.createElement("style");
    el.id = id;
    el.textContent = EMPTY_CSS;
    document.head.appendChild(el);
  }
}

// ── Props ─────────────────────────────────────────────────────────────────────

export interface EmptyLibraryStateProps {
  /** Opens the new-recipe creation flow. */
  onCreateRecipe: () => void;
  /**
   * SPA navigate callback to /app/help. When provided, the "Syntax reference"
   * link in the first-run callout uses SPA navigation instead of a full-page
   * reload. The production caller (LibraryView) provides this via useNavigate;
   * standalone renders (tests that mount without a router) omit it and the
   * link falls back to its href="/app/help".
   */
  onOpenSyntaxReference?: () => void;
  /**
   * Beat-0 entry fork (plan T3) — the "worked example" arm. When provided, a
   * secondary "Open a worked example" action renders beside "Add recipe" and
   * adopts the napoletana seed via `recipe.spinOff`. The parent owns the RPC +
   * navigation + the governed first-recipe Pip moment/toast; this component owns
   * the busy state, the time-bound, and the visible failure (Rule 8). When
   * absent (e.g. tests / offline), the arm is not rendered — no dead affordance.
   */
  onOpenWorkedExample?: () => Promise<void>;
}

// ── WorkedExampleAction — Beat-0 seed-adoption arm ─────────────────────────────
//
// Rule 8: a mutate action must surface its outcome. This owns:
//   - a busy state ("Opening…", disabled) so the tap cannot double-fire;
//   - a time-bound via withTimeout so a stalled spin-off can never latch the
//     busy state on forever;
//   - a quiet, visible inline error on failure (branding.md §5 — no
//     exclamations, no banned words).
// On success the parent navigates into the new recipe and this surface unmounts,
// so the busy state is cleared by unmount (no setState-after-unmount).

function WorkedExampleAction({ onOpen }: { onOpen: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClick = useCallback(() => {
    if (busy) return;
    setBusy(true);
    setError(null);
    void (async () => {
      try {
        // 15s bound — a spin-off round-trips D1 + the caller's DO; generous but
        // never infinite (Rule 8: the pending state must resolve).
        await withTimeout(onOpen(), 15_000, "Open worked example");
        // Success: parent has navigated into the new recipe; leave `busy` latched
        // and let the unmount clear it.
      } catch (err) {
        console.error({ event: "worked_example_open_failed", err });
        setError("Could not open the example. Try again.");
        setBusy(false);
      }
    })();
  }, [busy, onOpen]);

  return (
    <>
      <button
        type="button"
        className="proof-worked-example"
        style={workedExampleStyle}
        onClick={handleClick}
        disabled={busy}
        aria-busy={busy}
        data-testid="open-worked-example"
      >
        {busy ? "Opening…" : "Open a worked example"}
      </button>
      {error != null && (
        <p style={workedExampleErrorStyle} role="alert" data-testid="worked-example-error">
          {error}
        </p>
      )}
    </>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export function EmptyLibraryState({ onCreateRecipe }: EmptyLibraryStateProps) {
  return (
    <div style={wrapperStyle} data-testid="empty-library-state">
      {/* One declarative line — no exclamation, no banned words, sentence-case. */}
      <p style={messageStyle}>Your library is empty.</p>
      <button
        type="button"
        className="proof-empty-library-cta"
        style={ctaStyle}
        onClick={onCreateRecipe}
        data-testid="create-first-recipe-cta"
      >
        Add recipe
      </button>
    </div>
  );
}

// ── P1 — EmptyLibraryStateWithPip ──────────────────────────────────────────────
//
// Placement P1 (plan §4): 64px resting Pip with idle breath animation above
// the existing empty-state copy. No copy changes. Layout shift: zero — the Pip
// slot is inserted above the paragraph without reflowing the CTA.
//
// design.md §13a.2 amendment (T-P0): "No illustrations or icons except Pip,
// the product mascot…" — the exception that makes this legal.
// Pip is aria-hidden here: the copy below is the announcement.

export function EmptyLibraryStateWithPip({
  onCreateRecipe,
  onOpenSyntaxReference,
  onOpenWorkedExample,
}: EmptyLibraryStateProps) {
  // First-run callout state is self-contained here: empty library (this
  // component is mounted) AND the dismiss flag is unset. No app.tsx or
  // create-handler changes — see .plan/features/2026-06-14-firstrun-onboarding.md.
  const { show: showCallout, dismiss: dismissCallout } = useFirstRunCallout();

  return (
    <div style={wrapperStyle} data-testid="empty-library-state">
      {/*
       * First-run context callout — shown only when the library is empty and
       * the user has not dismissed it. Sits ABOVE Pip + message + CTA. Hairline
       * card, no accent fill: the "Add recipe" CTA below is the one accent.
       *
       * onOpenSyntaxReference: when provided (production path — LibraryView
       * supplies a useNavigate callback), the "Syntax reference" link uses SPA
       * navigation. When absent (standalone renders / tests without a router),
       * FirstRunCallout falls back to its href="/app/help" anchor. This keeps
       * the component itself router-free so the P1 Pip test (no MemoryRouter)
       * stays green.
       */}
      {showCallout && (
        <FirstRunCallout
          onDismiss={dismissCallout}
          {...(onOpenSyntaxReference !== undefined && { onOpenSyntaxReference })}
        />
      )}
      {/*
       * Pip at 64px, resting idle-breath pose.
       * aria-hidden: the copy below is the surface announcement.
       * Rule 7: 64px renders fine at 375px (fills no more than 17% of width).
       */}
      <PipMascot size={64} ariaHidden />
      {/* One declarative line — no exclamation, no banned words, sentence-case. */}
      <p style={messageStyle}>Your library is empty.</p>
      {/*
       * Beat-0 entry fork (plan T3): two ways forward.
       *   - "Add recipe" — start from a blank canvas (the one accent fill).
       *   - "Open a worked example" — adopt the napoletana seed so a brand-new
       *     user has a real, computing recipe to learn from. The arm renders only
       *     when the parent wires `onOpenWorkedExample` (no dead affordance).
       */}
      <div style={forkStyle}>
        <button
          type="button"
          className="proof-empty-library-cta"
          style={{ ...ctaStyle, width: "100%", justifyContent: "center" }}
          onClick={onCreateRecipe}
          data-testid="create-first-recipe-cta"
        >
          Add recipe
        </button>
        {onOpenWorkedExample !== undefined && <WorkedExampleAction onOpen={onOpenWorkedExample} />}
      </div>
    </div>
  );
}
